import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import {
  Dropzone,
  DropzoneGroup,
  DropzoneInput,
  DropzoneTitle,
  DropzoneUploadIcon,
  DropzoneZone,
} from "./ui/dropzone";
import { InfoIcon, PrinterIcon } from "lucide-react";
import {
  FileList,
  FileListItem,
  FileListHeader,
  FileListInfo,
  FileListIcon,
  FileListName,
  FileListDescription,
  FileListSize,
  FileListDescriptionSeparator,
  FileListDescriptionText,
  FileListContent,
  FileListProgress,
} from "./ui/file-list";
import {
  MandateApiFailure,
  uploadMandate,
} from "./services/mandate.service";
import { pdf } from "@react-pdf/renderer";
import DirectDebitMandatePDF from "./DirectDebitMandatePDF";

interface DirectDebitDownloadProps {
  data?: any;
  handleBackButton?: () => void;
  handleSubmitButton?: () => void;
  printOnly?: any;
  disabled?: boolean;
  token?: string;
  onFileUploaded?: (file: File) => void;
  onSigningTokenFailure?: (failure: MandateApiFailure) => void;
}

const DirectDebitDownload: React.FC<DirectDebitDownloadProps> = ({
  data,
  handleBackButton,
  handleSubmitButton,
  printOnly,
  disabled = false,
  token,
  onFileUploaded,
  onSigningTokenFailure,
}) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    // Enable button when file is selected
    setButtonDisabled(files.length === 0);
  }, [files]);

  const handlePrintButton = async () => {
    // Generate the PDF as a blob
    const blob = await pdf(<DirectDebitMandatePDF data={data} />).toBlob();

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Open the Blob in a new window with security protection
    const newWindow = window.open(url, "_blank", "noopener");
    if (newWindow) {
      newWindow.opener = null;
    }

    // Revoke the URL after the window has had time to load the blob
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setFiles(acceptedFiles);
    setProgress(0);
  };

  const handleUploadAndSubmit = async () => {
    if (files.length === 0 || !token) {
      console.error("No file selected or token missing");
      return;
    }

    const file = files[0];
    setIsUploading(true);
    setProgress(0);

    try {
      const outcome = await uploadMandate(
        token,
        file,
        data?.terms || false, // soleSignatory from terms checkbox
        data?.signature || "", // signature field
        setProgress
      );
      if (!outcome.ok) {
        if (onSigningTokenFailure) {
          onSigningTokenFailure(outcome);
        } else {
          console.error("Upload failed:", outcome);
        }
        return;
      }
      // Store the uploaded file for later download
      if (onFileUploaded) {
        onFileUploaded(file);
      }
      // Call the parent's submit handler on successful upload
      if (handleSubmitButton) {
        handleSubmitButton();
      }
    } catch (error) {
      console.error("Upload error:", error);
      onSigningTokenFailure?.({
        ok: false,
        status: 0,
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsUploading(false);
    }
  };

  return !printOnly ? (
    <Card className="text-left max-w-3xl">
      <CardHeader>
        <div className="flex flex-row justify-between items-center">
          <h1 className="text-2xl mb-2">Direct Debit Mandate</h1>
        </div>
        <Separator></Separator>
      </CardHeader>
      <CardContent className="flex flex-col gap-8">
        <Button
          variant="yvrSecondary"
          disabled={disabled}
          className="rounded-none w-1/3 self-center gap-2 text-gray-700"
          onClick={handlePrintButton}
        >
          <PrinterIcon></PrinterIcon>
          Print Mandate
        </Button>
        {!printOnly ? (
          <>
            <div className="flex flex-row gap-4 items-center bg-gray-100 p-8 h-24">
              <InfoIcon size={24} className="min-w-6"></InfoIcon>
              <p className="text-sm text-gray-500 ">
                Please print a copy of your Direct Debit Mandate, ensure that
                all required signatures are added, then upload a PDF of the
                signed Mandate below.
              </p>
            </div>
            <Dropzone
              accept={{
                "application/pdf": [".pdf"],
              }}
              onDrop={handleDrop}
              multiple={false}
            >
              <div
                className={
                  disabled ? "grid gap-4 pointer-events-none" : "grid gap-4"
                }
              >
                <DropzoneZone>
                  <DropzoneInput disabled={disabled} />
                  <DropzoneGroup className="gap-4">
                    <DropzoneUploadIcon />
                    <DropzoneGroup>
                      <DropzoneTitle>
                        Drop file here or click to upload
                      </DropzoneTitle>
                    </DropzoneGroup>
                  </DropzoneGroup>
                </DropzoneZone>
                <FileList>
                  {files.map((file) => (
                    <FileListItem key={file.name}>
                      <FileListHeader>
                        <FileListIcon />
                        <FileListInfo>
                          <FileListName>{file.name}</FileListName>
                          <FileListDescription>
                            <FileListSize>{file.size}</FileListSize>
                            <FileListDescriptionSeparator />
                            <FileListDescriptionText>
                              {isUploading ? `${progress}%` : "Ready to upload"}
                            </FileListDescriptionText>
                          </FileListDescription>
                        </FileListInfo>
                      </FileListHeader>
                      <FileListContent>
                        <FileListProgress value={progress} />
                      </FileListContent>
                    </FileListItem>
                  ))}
                </FileList>
              </div>
            </Dropzone>
          </>
        ) : (
          <div className="flex flex-row gap-4 items-center bg-gray-100 p-8 h-24">
            <InfoIcon size={24}></InfoIcon>
            <p className="text-sm text-gray-500">
              Click here to print a copy of your Direct Debit Mandate. <br></br>
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex-col gap-6">
        <Separator></Separator>
        <div className="flex justify-end gap-2 w-full">
          {handleBackButton && !printOnly && (
            <Button variant="yvrSecondary" onClick={() => handleBackButton()}>
              Back
            </Button>
          )}
          <Button
            disabled={(buttonDisabled || isUploading) && !printOnly}
            variant="yvrPrimary"
            onClick={
              !printOnly
                ? handleUploadAndSubmit
                : () => {
                    window.close();
                  }
            }
          >
            {printOnly
              ? "Close"
              : isUploading
              ? "Uploading..."
              : "Upload Mandate"}
          </Button>
        </div>
      </CardFooter>
    </Card>
  ) : (
    <Button
      variant="yvrSecondary"
      disabled={disabled}
      className="rounded-none w-1/3 self-center gap-2 text-gray-700"
      onClick={handlePrintButton}
    >
      <PrinterIcon></PrinterIcon>
      Print Mandate
    </Button>
  );
};

export default DirectDebitDownload;
